import { useState, useEffect, useMemo, useRef } from 'react';
import { DialogShell } from './ui/DialogShell';
import { Select } from './ui/Select';
import { TextInput } from './ui/TextInput';
import { Diocesis, ProvinciaEclesiastica, provinciaEclesiasticaService } from '../services';
import { getLocationService } from '../services/locationServiceInstance';
import { diocesisEstadosService, type DiocesisEstadoConDiocesis } from '../services/diocesisEstadosService';
import type { State } from '../types/location';
import { ModalFormActions } from './ui/ModalFormActions';
import { Search } from 'lucide-react';
import { reportarErrorInesperado } from '../utils/reportarErrorInesperado';
import { matchesSearch } from '../utils/textNormalization';

interface DiocesisModalProps {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly onSave: (diocesis: Omit<Diocesis, 'id' | 'created_at' | 'provincia_eclesiastica' | 'cantidad_lumisiales'>) => Promise<string>;
  readonly diocesis?: Diocesis;
}

export function DiocesisModal({ isOpen, onClose, onSave, diocesis }: DiocesisModalProps) {
  const [formData, setFormData] = useState({
    nombre: '',
    tipo: 'diocesis' as 'diocesis' | 'prefectura',
    provincia_eclesiastica_id: '',
    activo: true
  });
  const [provinciasEclesiasticas, setProvinciasEclesiasticas] = useState<ProvinciaEclesiastica[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingProvinciasEclesiasticas, setLoadingProvinciasEclesiasticas] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Estado para selector multi-estado
  const [allStates, setAllStates] = useState<State[]>([]);
  const [selectedStateIso2s, setSelectedStateIso2s] = useState<Set<string>>(new Set());
  const [assignedStates, setAssignedStates] = useState<DiocesisEstadoConDiocesis[]>([]);
  const [loadingStates, setLoadingStates] = useState(false);
  const [stateSearch, setStateSearch] = useState('');

  const hasFormChanges = (): boolean => {
    if (!diocesis) {
      return formData.nombre !== '' || formData.tipo !== 'diocesis' || formData.provincia_eclesiastica_id !== '' || !formData.activo;
    }
    return formData.nombre !== diocesis.nombre ||
      formData.tipo !== diocesis.tipo ||
      formData.activo !== diocesis.activo;
  };

  useEffect(() => {
    if (isOpen) {
      cargarProvinciasEclesiasticas();
    }
  }, [isOpen]);

  useEffect(() => {
    if (diocesis) {
      setFormData({
        nombre: diocesis.nombre,
        tipo: diocesis.tipo,
        provincia_eclesiastica_id: diocesis.provincia_eclesiastica_id,
        activo: diocesis.activo
      });
    } else {
      setFormData({ nombre: '', tipo: 'diocesis', provincia_eclesiastica_id: '', activo: true });
    }
    setErrors({});
    setStateSearch('');
  }, [diocesis, isOpen]);

  // Cargar estados cuando cambia la provincia eclesiástica seleccionada
  useEffect(() => {
    if (formData.provincia_eclesiastica_id) {
      cargarEstados(formData.provincia_eclesiastica_id);
    } else {
      setAllStates([]);
      setAssignedStates([]);
      setSelectedStateIso2s(new Set());
    }
  }, [formData.provincia_eclesiastica_id]);

  // Pre-cargar estados asignados al editar diócesis existente
  useEffect(() => {
    if (diocesis?.id && allStates.length > 0) {
      cargarEstadosAsignados(diocesis.id);
    }
  }, [diocesis?.id, allStates]);

  const cargarProvinciasEclesiasticas = async () => {
    try {
      setLoadingProvinciasEclesiasticas(true);
      const data = await provinciaEclesiasticaService.listarActivos();
      setProvinciasEclesiasticas(data);
      if (data.length > 0 && !formData.provincia_eclesiastica_id && !diocesis) {
        setFormData(prev => ({ ...prev, provincia_eclesiastica_id: data[0].id }));
      }
    } catch (error) {
      // El aviso junto al campo se queda: dice dónde falló. El reporte va por el toast.
      setErrors({ provincia_eclesiastica_id: 'No pudimos cargar las provincias eclesiásticas' });
      reportarErrorInesperado(error, 'No pudimos cargar las provincias eclesiásticas.');
    } finally {
      setLoadingProvinciasEclesiasticas(false);
    }
  };

  const cargarEstados = async (provinciaId: string) => {
    try {
      setLoadingStates(true);
      const provincia = provinciasEclesiasticas.find(p => p.id === provinciaId);
      if (!provincia?.codigo_iso) {
        setAllStates([]);
        setAssignedStates([]);
        return;
      }

      const [states, assigned] = await Promise.all([
        getLocationService().getStates(provincia.codigo_iso),
        diocesisEstadosService.listarAsignadosPorProvincia(provinciaId),
      ]);

      setAllStates(states);
      setAssignedStates(assigned);

      // Si no estamos editando, limpiar selección
      if (!diocesis) {
        setSelectedStateIso2s(new Set());
      }
    } catch (error) {
      reportarErrorInesperado(error, 'No pudimos cargar las regiones.');
    } finally {
      setLoadingStates(false);
    }
  };

  const cargarEstadosAsignados = async (diocesisId: string) => {
    try {
      const estadosDiocesis = await diocesisEstadosService.listarPorDiocesis(diocesisId);
      setSelectedStateIso2s(new Set(estadosDiocesis.map(e => e.estado_iso2)));
    } catch (error) {
      reportarErrorInesperado(error, 'No pudimos cargar las regiones asignadas a la diócesis.');
    }
  };

  const toggleState = (iso2: string) => {
    setSelectedStateIso2s(prev => {
      const next = new Set(prev);
      if (next.has(iso2)) {
        next.delete(iso2);
      } else {
        next.add(iso2);
      }
      return next;
    });
  };

  /** Mapa de estados asignados a OTRAS diócesis (no la actual) */
  const assignedToOtherMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const a of assignedStates) {
      if (a.diocesis_id !== diocesis?.id) {
        map.set(a.estado_iso2, a.diocesis_nombre || 'Otra diócesis');
      }
    }
    return map;
  }, [assignedStates, diocesis?.id]);

  const filteredStates = useMemo(() => {
    if (!stateSearch.trim()) return allStates;
    return allStates.filter(s => matchesSearch(s.name, stateSearch) || matchesSearch(s.iso2, stateSearch));
  }, [allStates, stateSearch]);

  const selectedProvinciaIso = useMemo(() => {
    const prov = provinciasEclesiasticas.find(p => p.id === formData.provincia_eclesiastica_id);
    return prov?.codigo_iso || '';
  }, [provinciasEclesiasticas, formData.provincia_eclesiastica_id]);

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!formData.nombre.trim()) newErrors.nombre = 'El nombre es requerido';
    if (!formData.provincia_eclesiastica_id) newErrors.provincia_eclesiastica_id = 'Debe seleccionar una provincia eclesiástica';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    try {
      setLoading(true);
      const diocesisId = await onSave(formData);

      // Persistir asociaciones de estados
      if (selectedProvinciaIso) {
        const estadosToSave = allStates
          .filter(s => selectedStateIso2s.has(s.iso2))
          .map(s => ({ estado_iso2: s.iso2, pais_iso2: selectedProvinciaIso, estado_nombre: s.name }));
        await diocesisEstadosService.guardarAsociaciones(diocesisId, estadosToSave);
      }

      onClose();
    } catch (error) {
      reportarErrorInesperado(error, 'No pudimos guardar la diócesis.');
    } finally {
      setLoading(false);
    }
  };

  const closeRef = useRef<(() => void) | null>(null);
  const requestClose = () => closeRef.current?.();

  return (
    <DialogShell
      isOpen={isOpen}
      onClose={onClose}
      hasUnsavedChanges={hasFormChanges()}
      closeRef={closeRef}
      title={diocesis ? 'Editar Diócesis/Prefectura' : 'Agregar Diócesis/Prefectura'}
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <TextInput
          label="Nombre *"
          value={formData.nombre}
          onChange={(v) => setFormData({ ...formData, nombre: v })}
          placeholder="Ej: Sur"
          error={errors.nombre}
        />

        <div>
          <span className="block text-sm font-medium text-slate-700 mb-1">
            Tipo *
          </span>
          <Select
            value={formData.tipo}
            onChange={(value) => setFormData({ ...formData, tipo: value as 'diocesis' | 'prefectura' })}
            options={[
              { value: 'diocesis', label: 'Diócesis' },
              { value: 'prefectura', label: 'Prefectura' }
            ]}
          />
          <p className="mt-1 text-xs text-slate-500">
            Una Prefectura es una Diócesis en formación (menos de 6 lumisiales)
          </p>
        </div>

        <div>
          <span className="block text-sm font-medium text-slate-700 mb-1">
            Provincia Eclesiástica *
          </span>
          {loadingProvinciasEclesiasticas ? (
            <div className="flex items-center justify-center py-2">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-slate-700"></div>
            </div>
          ) : (
            <Select
              value={formData.provincia_eclesiastica_id}
              onChange={(value) => {
                setFormData({ ...formData, provincia_eclesiastica_id: value });
                setSelectedStateIso2s(new Set());
              }}
              options={provinciasEclesiasticas.map(p => ({ value: p.id, label: p.nombre }))}
              placeholder="Seleccionar provincia eclesiástica"
              error={errors.provincia_eclesiastica_id}
            />
          )}
        </div>

        {/* Selector multi-estado */}
        {formData.provincia_eclesiastica_id && (
          <div>
            <span className="block text-sm font-medium text-slate-700 mb-1">
              Estados/Regiones <span className="text-slate-400 font-normal">(opcional)</span>
            </span>
            {loadingStates && (
              <div className="flex items-center justify-center py-4">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-slate-700"></div>
              </div>
            )}
            {!loadingStates && allStates.length === 0 && (
              <p className="text-sm text-slate-500 py-2">No hay estados disponibles para este país</p>
            )}
            {!loadingStates && allStates.length > 0 && (
              <div className="border border-slate-200 rounded-lg overflow-hidden">
                <div className="p-2 border-b border-slate-200">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                      type="text"
                      value={stateSearch}
                      onChange={(e) => setStateSearch(e.target.value)}
                      placeholder="Buscar estado..."
                      className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
                    />
                  </div>
                </div>
                <div className="max-h-48 overflow-y-auto divide-y divide-slate-100">
                  {filteredStates.map(state => {
                    const assignedTo = assignedToOtherMap.get(state.iso2);
                    const isDisabled = !!assignedTo;
                    const isChecked = selectedStateIso2s.has(state.iso2);
                    return (
                      <label
                        key={state.iso2}
                        className={`flex items-center px-3 py-2 text-sm ${
                          isDisabled ? 'opacity-50 cursor-not-allowed bg-slate-50' : 'cursor-pointer hover:bg-slate-50'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={isChecked}
                          disabled={isDisabled}
                          onChange={() => { if (!isDisabled) toggleState(state.iso2); }}
                          className="w-4 h-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500 mr-2"
                        />
                        <span className="flex-1">{state.name}</span>
                        <span className="text-xs text-slate-400 ml-2 font-mono">{state.iso2}</span>
                        {assignedTo && (
                          <span className="text-xs text-amber-600 ml-2">Asignado a {assignedTo}</span>
                        )}
                      </label>
                    );
                  })}
                </div>
                {selectedStateIso2s.size > 0 && (
                  <div className="px-3 py-2 bg-blue-50 border-t border-slate-200 text-xs text-blue-700">
                    {selectedStateIso2s.size} estado{selectedStateIso2s.size === 1 ? '' : 's'} seleccionado{selectedStateIso2s.size === 1 ? '' : 's'}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        <div className="flex items-center">
          <input
            type="checkbox"
            id="activo"
            checked={formData.activo}
            onChange={(e) => setFormData({ ...formData, activo: e.target.checked })}
            className="w-4 h-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500"
          />
          <label htmlFor="activo" className="ml-2 text-sm text-slate-700">
            Diócesis/Prefectura activa
          </label>
        </div>

        {diocesis?.cantidad_lumisiales !== undefined && (
          <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg">
            <p className="text-sm text-slate-600">
              <span className="font-medium">Lumisiales asociados:</span> {diocesis.cantidad_lumisiales}
            </p>
          </div>
        )}

        <ModalFormActions
          onCancel={requestClose}
          loading={loading}
          disabled={loadingProvinciasEclesiasticas || loadingStates}
          submitLabel={diocesis ? 'Actualizar' : 'Crear'}
        />
      </form>
    </DialogShell>
  );
}
